import { faVideo as duofaVideo } from "@fortawesome/pro-duotone-svg-icons"
import { faBullseye } from "@fortawesome/pro-regular-svg-icons"
import {
  faBell,
  faCircleHalfStroke,
  faCirclePause,
  faCirclePlay,
  faEmptySet,
  faFlag,
  faPenToSquare,
  faSquareCheck,
  faSquarePlus,
  faSquareX,
  faVideo,
  faVideoSlash
} from "@fortawesome/pro-solid-svg-icons"
import { translate } from "app/language/service"
import { getStore } from "app/store/store"
import { deepCopy } from "app/tools/common"
import { dateSubstract, getDateWithMinTime, isDateAfter } from "app/util/date/tools/common"
import { getCachedCampaignName } from "campaign/tools/common"
import { getStatusColor, getStatusIconProp } from "campaign/tools/status"
import { getCachedMediaInfo } from "media/tools/common"
import { messageSVC } from "message/service"
import {
  INotficationSetting,
  INotification,
  INotificationInfo,
  INotificationItem,
  INotificationSettings,
  INotificationType,
  INotificationsByDateRange
} from "notification/model"
import { notificationRPO } from "notification/repository"
import { notificationActionCreator } from "notification/store/actions"
import { CAMPAIGN_MEDIA_PATH, CAMPAIGN_SUMMARY_PATH } from "page/model"
import { navigate } from "page/tools/navigate"
import { IActionButton } from "ui/model"

export const loadNotificationSettings = async (): Promise<void> => {
  if (!getStore().getState().notification.isLoadingSettings) {
    getStore().dispatch(notificationActionCreator.setIsLoadingNotificationSettings(true))
  }
  try {
    const settings = await notificationRPO.getNotificationSettings()
    getStore().dispatch(notificationActionCreator.notificationSettingsUpdated(settings))
  } catch (error) {
    console.error(error)
    getStore().dispatch(notificationActionCreator.setHasLoadingNotificationSettingsError(true))
  }
}

export const loadNotifications = async (): Promise<void> => {
  if (!getStore().getState().notification.isLoadingNewNotification) {
    getStore().dispatch(notificationActionCreator.setIsLoadingNotifications(true))
  }
  try {
    const notifications = await notificationRPO.getNotifications()
    getStore().dispatch(notificationActionCreator.addNotifications(notifications))
  } catch (error) {
    console.error(error)
    getStore().dispatch(notificationActionCreator.setHasLoadingNotificationsError(true))
  }
}

export const setNotificationSetting = (
  category: keyof INotificationSettings,
  type: INotificationType,
  value: INotficationSetting
): void => {
  const newSettings: any = { ...getStore().getState().notification.clientSettings }
  newSettings[category] = { ...newSettings[category] }
  newSettings[category][type] = value
  getStore().dispatch(notificationActionCreator.notificationSettingsChange(newSettings))
}

export const cancelSettings = () => {
  messageSVC.confirmMessage({
    message: [translate("label.will.cancel"), translate("label.continue.confirmation")],
    title: translate("label.input.inline.cancel"),
    onSuccess: async () => {
      getStore().dispatch(
        notificationActionCreator.notificationSettingsUpdated(deepCopy(getStore().getState().notification.serverSettings))
      )
    }
  })
}

export const updateSettings = async (settings: INotificationSettings) => {
  messageSVC.confirmMessage({
    message: [translate("notification.settings.update.confirmation"), translate("label.continue.confirmation")],
    title: translate("notification.settings.update"),
    onSuccess: async () => {
      try {
        if (!getStore().getState().notification.isUpdatingSettings) {
          getStore().dispatch(notificationActionCreator.setIsUpdatingNotificationSettings(true))
        }
        await notificationRPO.setNotificationSettings(settings)
        getStore().dispatch(notificationActionCreator.notificationSettingsUpdated(settings))
      } catch (error) {
        console.error(error)
        messageSVC.error(translate("notification.settings.update.error"))
        getStore().dispatch(notificationActionCreator.setIsUpdatingNotificationSettings(false))
      }
    }
  })
}

export const isNotificationSliderOpened = (): boolean => getStore().getState().notification.isSliderOpened

export const setNotificationSliderVisibility = (isOpened: boolean) =>
  getStore().dispatch(notificationActionCreator.setSliderVisibility(isOpened))

export const toggleNotificationSliderVisibility = () =>
  getStore().dispatch(notificationActionCreator.setSliderVisibility(!getStore().getState().notification.isSliderOpened))

export const getNotificationsByDateRange = (notifications: INotification[]): INotificationsByDateRange => {
  const result: INotificationsByDateRange = {
    today: [],
    yesterday: [],
    lastWeek: [],
    lastMonth: [],
    noLimit: []
  }
  const today = getDateWithMinTime(new Date())
  const yesterday = dateSubstract(today, 1, "days")
  const lastWeek = dateSubstract(today, 1, "weeks")
  const lastMonth = dateSubstract(today, 1, "months")
  for (const notification of notifications) {
    if (isDateAfter(notification.created_on, today, true)) {
      result.today.push(notification)
    } else if (isDateAfter(notification.created_on, yesterday, true)) {
      result.yesterday.push(notification)
    } else if (isDateAfter(notification.created_on, lastWeek, true)) {
      result.lastWeek.push(notification)
    } else if (isDateAfter(notification.created_on, lastMonth, true)) {
      result.lastMonth.push(notification)
    } else {
      result.noLimit.push(notification)
    }
  }
  return result
}

export const getNotificationItem = (notification: INotification): INotificationItem => {
  const type = notification.type
  const uiConfig: INotificationItem = {
    icon: faBell,
    color: "white"
  }
  switch (type) {
    case "campaign_pause_change": {
      const data: any = JSON.parse(notification.data || "{}")
      uiConfig.icon = data.pause ? faCirclePause : faCirclePlay
      uiConfig.color = "#71cc15"
      break
    }
    case "campaign_global_status_change": {
      const data: any = JSON.parse(notification.data || "{}")
      uiConfig.icon = getStatusIconProp(data.status)
      uiConfig.color = getStatusColor(data.status)
      break
    }
    case "campaign_report_ready": {
      uiConfig.icon = faFlag
      uiConfig.color = "#5abd51"
      break
    }
    case "campaign_is_update": {
      uiConfig.icon = faPenToSquare
      uiConfig.color = "#526d4f"
      break
    }
    case "media_approbation": {
      uiConfig.icon = faSquareCheck
      uiConfig.color = "#5abd51"
      break
    }
    case "media_rejection": {
      uiConfig.icon = faSquareX
      uiConfig.color = "#bf3623"
      break
    }
    case "campaign_budget_add": {
      uiConfig.icon = faSquarePlus
      uiConfig.color = "#5abd51"
      break
    }
    case "campaign_budget_empty": {
      uiConfig.icon = faEmptySet
      uiConfig.color = "#bf3623"
      break
    }
    case "campaign_budget_half": {
      uiConfig.icon = faCircleHalfStroke
      uiConfig.color = "#a77b75"
      break
    }
    case "campaign_impression_start": {
      uiConfig.icon = faVideo
      uiConfig.color = "#5abd51"
      break
    }
    case "campaign_impression_missing": {
      uiConfig.icon = faVideoSlash
      uiConfig.color = "#bf3623"
      break
    }
    case "campaign_impression_half": {
      uiConfig.icon = duofaVideo
      uiConfig.color = "#a77b75"
      break
    }
  }
  return uiConfig
}

export const getNotificationInfo = async (notification: INotification): Promise<INotificationInfo> => {
  const type = notification.type
  const notificationInfo: INotificationInfo = {
    message: "",
    entity_name: "",
    actions: []
  }
  const data: any = JSON.parse(notification.data || "{}")
  switch (type) {
    case "campaign_pause_change": {
      const campaignName = await getCachedCampaignName(notification.campaign_id as number)
      notificationInfo.message = translate(`notification.type.${type}.${data.pause}.message`, campaignName)
      notificationInfo.actions.push(getCampaignLinkAction(notification.campaign_id as number))
      break
    }
    case "campaign_global_status_change": {
      const campaignName = await getCachedCampaignName(notification.campaign_id as number)
      notificationInfo.message = translate(`notification.type.${type}.message`, [
        campaignName,
        translate(`status.${data.status}`)
      ])
      notificationInfo.actions.push(getCampaignLinkAction(notification.campaign_id as number))
      break
    }
    case "campaign_report_ready": {
      const campaignName = await getCachedCampaignName(notification.campaign_id as number)
      notificationInfo.message = translate(`notification.type.${type}.message`, campaignName)
      notificationInfo.actions.push(getCampaignLinkAction(notification.campaign_id as number))
      break
    }
    case "campaign_is_update": {
      const campaignName = await getCachedCampaignName(notification.campaign_id as number)
      notificationInfo.message = translate(`notification.type.${type}.message`, campaignName)
      notificationInfo.actions.push(getCampaignLinkAction(notification.campaign_id as number))
      break
    }
    case "media_approbation": {
      const mediaInfo = await getCachedMediaInfo(notification.media_id as number)
      notificationInfo.message = translate(`notification.type.${type}.message`, mediaInfo.name)
      notificationInfo.actions.push(
        getMediaLinkAction(notification.campaign_id as number, notification.media_id as number)
      )
      break
    }
    case "media_rejection": {
      const mediaInfo = await getCachedMediaInfo(notification.media_id as number)
      notificationInfo.message = translate(`notification.type.${type}.message`, mediaInfo.name)
      notificationInfo.actions.push(
        getMediaLinkAction(notification.campaign_id as number, notification.media_id as number)
      )
      break
    }
    case "campaign_budget_add": {
      const campaignName = await getCachedCampaignName(notification.campaign_id as number)
      notificationInfo.message = translate(`notification.type.${type}.message`, campaignName)
      notificationInfo.actions.push(getCampaignLinkAction(notification.campaign_id as number))
      break
    }
    case "campaign_budget_empty": {
      const campaignName = await getCachedCampaignName(notification.campaign_id as number)
      notificationInfo.message = translate(`notification.type.${type}.message`, campaignName)
      notificationInfo.actions.push(getCampaignLinkAction(notification.campaign_id as number))
      break
    }
    case "campaign_budget_half": {
      const campaignName = await getCachedCampaignName(notification.campaign_id as number)
      notificationInfo.message = translate(`notification.type.${type}.message`, campaignName)
      notificationInfo.actions.push(getCampaignLinkAction(notification.campaign_id as number))
      break
    }
    case "campaign_impression_start": {
      const campaignName = await getCachedCampaignName(notification.campaign_id as number)
      notificationInfo.message = translate(`notification.type.${type}.message`, campaignName)
      notificationInfo.actions.push(getCampaignLinkAction(notification.campaign_id as number))
      break
    }
    case "campaign_impression_missing": {
      const campaignName = await getCachedCampaignName(notification.campaign_id as number)
      notificationInfo.message = translate(`notification.type.${type}.message`, campaignName)
      notificationInfo.actions.push(getCampaignLinkAction(notification.campaign_id as number))
      break
    }
    case "campaign_impression_half": {
      const campaignName = await getCachedCampaignName(notification.campaign_id as number)
      notificationInfo.message = translate(`notification.type.${type}.message`, campaignName)
      notificationInfo.actions.push(getCampaignLinkAction(notification.campaign_id as number))
      break
    }
  }
  return notificationInfo
}

export const markAllNotificationsAsRead = async (): Promise<void> => {
  try {
    await notificationRPO.setAsRead([])
    getStore().dispatch(notificationActionCreator.setNotificationsAsRead([]))
  } catch (error) {
    console.error(error)
    messageSVC.error(translate("notification.mark.all.as.read.error"))
  }
}

export const deleteAllNotifications = async (): Promise<void> => {
  try {
    await notificationRPO.deleteNotifications([])
    getStore().dispatch(notificationActionCreator.addNotifications([]))
  } catch (error) {
    console.error(error)
    messageSVC.error(translate("notification.mark.all.as.read.error"))
  }
}

const getCampaignLinkAction = (campaignId: number): IActionButton => ({
  icon: faBullseye,
  label: translate("campaign.summary.tooltip.title"),
  onClick: () => navigate({ path: CAMPAIGN_SUMMARY_PATH, pathParams: campaignId })
})

const getMediaLinkAction = (campaignId: number, mediaId: number): IActionButton => ({
  icon: faBullseye,
  label: translate("campaign.creative.show.full.details"),
  onClick: () => navigate({ path: CAMPAIGN_MEDIA_PATH, pathParams: [campaignId, mediaId] })
})
